import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { FreteLocalFields, cidadeUf } from "./FreteLocalFields";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { FilePlus2, Plus, Trash2, Download, FileText, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PRAZOS = [1, 6, 12, 24, 36, 48, 60];
const TIPOS = [
  { v: "locacao", l: "Locação" },
  { v: "venda", l: "Venda" },
  { v: "servico", l: "Serviço" },
  { v: "outros", l: "Outros" },
];

const fmtBRL = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const EMPTY_ITEM = {
  catalogo_id: "",
  nome: "",
  descricao: "",
  quantidade: 1,
  valor_mensal: 0,
  prazo_meses: 12,
  valor_referencia_compra: null,
  custo_manutencao_mensal_estimado: null,
};

const calcTotalItem = (item, tipo) => {
  const qtd = Number(item.quantidade) || 0;
  const v = Number(item.valor_mensal) || 0;
  const prazo = Number(item.prazo_meses) || 0;
  if (tipo === "venda") return qtd * v;
  return qtd * v * prazo;
};

export default function PropostaNativaForm({ lead, onCreated, prefillPedidoId = null, triggerLabel = null, triggerIcon = null, forceEnable = false }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1=cliente · 2=itens · 3=condições · 4=modelo+resumo
  const [templates, setTemplates] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [condicoes, setCondicoes] = useState([]);
  const [modelosContrato, setModelosContrato] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [propostaSalva, setPropostaSalva] = useState(null);
  const [origemPedido, setOrigemPedido] = useState(null);
  // Busca de inventário (Etapa 2)
  const [showInvSearch, setShowInvSearch] = useState(false);
  const [invQ, setInvQ] = useState("");
  const [invPropriedade, setInvPropriedade] = useState("");
  const [invResults, setInvResults] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [aiLoadingSolucao, setAiLoadingSolucao] = useState(false);
  const [aiLoadingItem, setAiLoadingItem] = useState(null); // index do item sendo processado

  const [form, setForm] = useState({
    template_id: "",
    tipo_contrato: "locacao",
    cliente_nome: lead?.empresa || lead?.nome || "",
    cliente_email: lead?.email || "",
    cliente_documento: lead?.cnpj || "",
    cliente_telefone: lead?.telefone || "",
    cliente_endereco: "",
    descricao_solucao: "",
    validade_dias: 15,
    forma_pagamento: "",
    condicao_pagamento_id: "",
    condicoes_pagamento: "",
    modelo_contrato_id: "",
    observacoes: "",
    testemunha_1_nome: "",
    testemunha_1_cpf: "",
    testemunha_2_nome: "",
    testemunha_2_cpf: "",
    frete: "",
    local_operacao: cidadeUf(lead),
    itens: [],
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const calls = [
      api.get("/proposta/templates", { params: { ativo: true } }),
      api.get("/proposta/catalogo", { params: { ativo: true } }),
      api.get("/financeiro/condicoes-pagamento", { params: { ativo: true } }),
      api.get("/configuracoes/modelos-contrato", { params: { ativo: true } }),
    ];
    if (prefillPedidoId) {
      calls.push(api.get(`/proposta-nativa/prefill-from-pedido/${prefillPedidoId}`));
    }
    Promise.all(calls)
      .then((results) => {
        const [t, c, cond, modelos, pre] = results;
        setTemplates(t.data || []);
        setCatalogo(c.data || []);
        setCondicoes(cond.data || []);
        setModelosContrato(modelos.data || []);
        // Auto-selecionar modelo padrão do tipo_contrato atual
        const padrao = (modelos.data || []).find((m) => m.padrao && m.tipo_contrato === "locacao");
        if (padrao) setForm((f) => ({ ...f, modelo_contrato_id: f.modelo_contrato_id || padrao.id }));
        if (pre && pre.data) {
          const d = pre.data;
          setOrigemPedido(d._pedido_resumo || { id: prefillPedidoId });
          setForm((f) => ({
            ...f,
            tipo_contrato: d.tipo_contrato || f.tipo_contrato,
            cliente_nome: d.cliente_nome || f.cliente_nome,
            cliente_email: d.cliente_email || f.cliente_email,
            cliente_documento: d.cliente_documento || f.cliente_documento,
            cliente_telefone: d.cliente_telefone || f.cliente_telefone,
            cliente_endereco: d.cliente_endereco || f.cliente_endereco,
            observacoes: d.observacoes || f.observacoes,
            itens: Array.isArray(d.itens) && d.itens.length ? d.itens : f.itens,
          }));
          toast.success("Dados do pedido carregados na proposta");
        }
      })
      .catch(() => toast.error("Falha ao carregar templates/catálogo"))
      .finally(() => setLoading(false));
  }, [open, prefillPedidoId]);

  const aplicarTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setForm((f) => ({
      ...f,
      template_id: tid,
      tipo_contrato: t.tipo_contrato || f.tipo_contrato,
      descricao_solucao: t.descricao_solucao || f.descricao_solucao,
      condicoes_pagamento: t.condicoes_padrao || f.condicoes_pagamento,
      observacoes: t.observacoes_padrao || f.observacoes,
    }));
  };

  const adicionarItem = () => {
    setForm((f) => ({ ...f, itens: [...f.itens, { ...EMPTY_ITEM }] }));
  };

  const removerItem = (idx) => {
    setForm((f) => ({ ...f, itens: f.itens.filter((_, i) => i !== idx) }));
  };

  const aplicarCatalogo = (idx, catId) => {
    if (!catId) {
      atualizarItem(idx, { catalogo_id: "" });
      return;
    }
    const c = catalogo.find((x) => x.id === catId);
    if (!c) return;
    atualizarItem(idx, {
      catalogo_id: catId,
      nome: c.nome,
      descricao: c.descricao || "",
      valor_mensal: c.valor_mensal_sugerido || 0,
      valor_referencia_compra: c.valor_referencia_compra ?? null,
      custo_manutencao_mensal_estimado: c.custo_manutencao_mensal_estimado ?? null,
    });
  };

  const atualizarItem = (idx, patch) => {
    setForm((f) => {
      const itens = [...f.itens];
      itens[idx] = { ...itens[idx], ...patch };
      return { ...f, itens };
    });
  };

  const totalMensal = form.itens.reduce(
    (acc, it) => acc + (Number(it.quantidade) || 0) * (Number(it.valor_mensal) || 0),
    0
  );
  const totalGeral = form.itens.reduce((acc, it) => acc + calcTotalItem(it, form.tipo_contrato), 0);

  const validar = () => {
    if (!form.cliente_nome.trim()) return "Nome do cliente obrigatório";
    if (!form.cliente_email.trim()) return "E-mail do cliente obrigatório";
    if (!form.frete) return "Selecione o frete (CIF ou FOB)";
    if (form.itens.length === 0) return "Adicione ao menos um item";
    for (let i = 0; i < form.itens.length; i++) {
      const it = form.itens[i];
      if (!it.nome.trim()) return `Item ${i + 1}: nome obrigatório`;
      if (!it.valor_mensal || Number(it.valor_mensal) <= 0) return `Item ${i + 1}: valor inválido`;
    }
    return null;
  };

  const salvarEGerar = async () => {
    const erro = validar();
    if (erro) {
      toast.error(erro);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        lead_id: lead.id,
        template_id: form.template_id || null,
        tipo_contrato: form.tipo_contrato,
        cliente_nome: form.cliente_nome,
        cliente_email: form.cliente_email,
        cliente_documento: form.cliente_documento || null,
        cliente_telefone: form.cliente_telefone || null,
        cliente_endereco: form.cliente_endereco || null,
        descricao_solucao: form.descricao_solucao,
        validade_dias: Number(form.validade_dias) || 15,
        forma_pagamento: form.forma_pagamento || null,
        condicao_pagamento_id: form.condicao_pagamento_id || null,
        condicoes_pagamento: form.condicoes_pagamento,
        modelo_contrato_id: form.modelo_contrato_id || null,
        observacoes: form.observacoes,
        testemunha_1_nome: form.testemunha_1_nome || null,
        testemunha_1_cpf: form.testemunha_1_cpf || null,
        testemunha_2_nome: form.testemunha_2_nome || null,
        testemunha_2_cpf: form.testemunha_2_cpf || null,
        frete: form.frete,
        local_operacao: form.local_operacao || form.cliente_endereco || null,
        itens: form.itens.map((it) => ({
          catalogo_id: it.catalogo_id || null,
          equipamento_inventario_id: it.equipamento_inventario_id || null,
          nome: it.nome,
          descricao: it.descricao || null,
          quantidade: Number(it.quantidade) || 1,
          valor_mensal: Number(it.valor_mensal) || 0,
          prazo_meses: Number(it.prazo_meses) || 1,
          valor_referencia_compra: it.valor_referencia_compra || null,
          custo_manutencao_mensal_estimado: it.custo_manutencao_mensal_estimado || null,
        })),
      };
      const { data } = await api.post("/proposta-nativa", payload);
      // gerar PDF logo após criar
      await api.post(`/proposta-nativa/${data.id}/gerar-pdf`);
      setPropostaSalva(data);
      toast.success("Proposta criada e PDF gerado");
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar proposta");
    } finally {
      setSaving(false);
    }
  };

  const baixarPdf = async () => {
    if (!propostaSalva) return;
    try {
      const { data } = await api.get(`/proposta-nativa/${propostaSalva.id}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Proposta_${propostaSalva.numero || propostaSalva.id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Falha ao baixar PDF");
    }
  };

  const reset = () => {
    setStep(1);
    setPropostaSalva(null);
    setOrigemPedido(null);
    setShowInvSearch(false);
    setInvResults([]);
    setInvQ("");
    setInvPropriedade("");
    setForm({
      template_id: "",
      tipo_contrato: "locacao",
      cliente_nome: lead?.empresa || lead?.nome || "",
      cliente_email: lead?.email || "",
      cliente_documento: lead?.cnpj || "",
      cliente_telefone: lead?.telefone || "",
      cliente_endereco: "",
      descricao_solucao: "",
      validade_dias: 15,
      forma_pagamento: "",
      condicao_pagamento_id: "",
      condicoes_pagamento: "",
      modelo_contrato_id: "",
      observacoes: "",
      testemunha_1_nome: "",
      testemunha_1_cpf: "",
      testemunha_2_nome: "",
      testemunha_2_cpf: "",
      frete: "",
      local_operacao: cidadeUf(lead),
      itens: [],
    });
  };

  const buscarInventario = async () => {
    setInvLoading(true);
    try {
      const params = { limit: 30 };
      if (invQ.trim()) params.q = invQ.trim();
      if (invPropriedade) params.propriedade = invPropriedade;
      const { data } = await api.get("/inventario/equipamentos-disponiveis-proposta", { params });
      setInvResults(data.items || []);
    } catch {
      toast.error("Falha ao buscar inventário");
    } finally {
      setInvLoading(false);
    }
  };

  const adicionarDoInventario = (equip) => {
    const nome = equip.nome_exibicao || `Serial ${equip.serial}`;
    setForm((f) => ({
      ...f,
      itens: [
        ...f.itens,
        {
          ...EMPTY_ITEM,
          equipamento_inventario_id: String(equip.id),
          nome,
          descricao: equip.observacoes || "",
        },
      ],
    }));
    toast.success(`${nome} adicionado à proposta`);
  };

  const aplicarCondicao = (cid) => {
    const c = condicoes.find((x) => x.id === cid);
    setForm((f) => ({
      ...f,
      condicao_pagamento_id: cid,
      // Se o vendedor não escreveu nada custom ainda, sugere a descrição padrão
      condicoes_pagamento: f.condicoes_pagamento && f.condicoes_pagamento.trim()
        ? f.condicoes_pagamento
        : (c?.descricao || c?.nome || ""),
    }));
  };

  const gerarSolucaoIA = async () => {
    if (!lead?.id) return;
    setAiLoadingSolucao(true);
    try {
      const { data } = await api.post("/proposta-nativa/ai/gerar-solucao", {
        lead_id: lead.id,
        tipo_contrato: form.tipo_contrato,
        itens: form.itens.map((it) => ({
          nome: it.nome,
          quantidade: it.quantidade,
          descricao: it.descricao,
        })),
      });
      setForm((f) => ({ ...f, descricao_solucao: data.texto || "" }));
      toast.success("Texto gerado pela IA · revise antes de gerar o PDF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na IA");
    } finally {
      setAiLoadingSolucao(false);
    }
  };

  const gerarDescricaoItemIA = async (idx) => {
    const item = form.itens[idx];
    if (!item || !item.nome?.trim()) {
      toast.error("Preencha o nome do equipamento primeiro");
      return;
    }
    setAiLoadingItem(idx);
    try {
      const { data } = await api.post("/proposta-nativa/ai/gerar-descricao-item", {
        nome: item.nome,
        descricao: item.descricao || "",
        tipo_contrato: form.tipo_contrato,
      });
      setForm((f) => {
        const novosItens = [...f.itens];
        novosItens[idx] = { ...novosItens[idx], descricao: data.texto || "" };
        return { ...f, itens: novosItens };
      });
      toast.success("Descrição comercial gerada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na IA");
    } finally {
      setAiLoadingItem(null);
    }
  };

  // Disponibilidade: só na etapa "proposta" do funil (Levantamento de Proposta).
  // Exceção: quando aberto a partir de um pedido (forceEnable), sempre permite.
  const podeOferecer = forceEnable || lead?.status_funil === "proposta";

  if (!podeOferecer) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        data-testid="criar-proposta-disabled"
        title="Disponível apenas quando o lead estiver na etapa Levantamento de Proposta"
        className="opacity-60"
      >
        <FileText size={14} className="mr-2" /> Criar Proposta
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          data-testid={prefillPedidoId ? `criar-proposta-from-pedido-${prefillPedidoId}` : "criar-proposta-btn"}
          className="text-white"
          style={{ background: "#F97316" }}
          size="sm"
        >
          {triggerIcon || <FilePlus2 size={14} className="mr-2" />}
          {triggerLabel || "Criar Proposta"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {propostaSalva ? "Proposta gerada com sucesso" : "Criar Nova Proposta"}
          </DialogTitle>
        </DialogHeader>

        {propostaSalva ? (
          <div className="space-y-4">
            <Card className="p-6 bg-emerald-50 border-emerald-200">
              <div className="flex items-center gap-3 mb-3">
                <FileText size={32} className="text-emerald-700" />
                <div>
                  <div className="font-display text-lg font-bold text-emerald-900">
                    Proposta nº {propostaSalva.numero}
                  </div>
                  <div className="text-sm text-emerald-700">
                    {propostaSalva.cliente_nome} · {fmtBRL(propostaSalva.valor_total_geral)}
                  </div>
                </div>
              </div>
              <div className="text-sm text-emerald-800">
                A proposta foi salva no lead na aba <strong>Pedidos</strong>. O PDF está pronto para download.
              </div>
            </Card>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { reset(); }} data-testid="proposta-nova">
                Criar outra
              </Button>
              <Button onClick={baixarPdf} data-testid="proposta-baixar-pdf" className="text-white" style={{ background: "#1B3A6B" }}>
                <Download size={14} className="mr-2" /> Baixar PDF
              </Button>
            </div>
          </div>
        ) : loading ? (
          <div className="py-16 text-center text-muted-foreground">
            <Loader2 className="inline animate-spin mr-2" /> Carregando templates...
          </div>
        ) : (
          <div className="space-y-4">
            {origemPedido && (
              <div
                className="text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-900 flex items-center gap-2"
                data-testid="proposta-origem-pedido"
              >
                <FileText size={14} />
                <span>
                  Dados pré-carregados do Pedido <strong>{origemPedido.id?.slice(0, 8) || prefillPedidoId?.slice(0, 8)}</strong>
                  {origemPedido.vendedor_nome ? ` · ${origemPedido.vendedor_nome}` : ""}
                  {typeof origemPedido.valor_total === "number" ? ` · ${fmtBRL(origemPedido.valor_total)}` : ""}
                </span>
              </div>
            )}
            {/* Stepper visual */}
            <div className="flex items-center gap-2 text-xs">
              {[
                { n: 1, l: "Cliente" },
                { n: 2, l: "Itens" },
                { n: 3, l: "Condições" },
                { n: 4, l: "Modelo" },
              ].map((s, idx, arr) => (
                <React.Fragment key={s.n}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`flex items-center justify-center w-7 h-7 rounded-full font-bold ${
                        step >= s.n ? "text-white" : "bg-slate-200 text-slate-500"
                      }`}
                      style={step >= s.n ? { background: "#1B3A6B" } : {}}
                    >
                      {s.n}
                    </div>
                    <div className={`text-[10px] mt-1 ${step === s.n ? "font-bold text-[#1B3A6B]" : "text-muted-foreground"}`}>{s.l}</div>
                  </div>
                  {idx < arr.length - 1 && <div className={`flex-1 h-0.5 self-start mt-3.5 ${step > s.n ? "bg-[#1B3A6B]" : "bg-slate-200"}`} />}
                </React.Fragment>
              ))}
            </div>

            {/* STEP 1: Template + Cliente */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label>Template (opcional)</Label>
                  <select
                    data-testid="proposta-template"
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    value={form.template_id}
                    onChange={(e) => aplicarTemplate(e.target.value)}
                  >
                    <option value="">— Sem template (em branco) —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.nome} ({t.tipo_contrato})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tipo de contrato *</Label>
                    <select
                      data-testid="proposta-tipo"
                      className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                      value={form.tipo_contrato}
                      onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })}
                    >
                      {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Validade (dias)</Label>
                    <Input
                      data-testid="proposta-validade"
                      type="number"
                      min={1}
                      max={365}
                      value={form.validade_dias}
                      onChange={(e) => setForm({ ...form, validade_dias: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Nome do cliente / empresa *</Label>
                  <Input
                    data-testid="proposta-cliente-nome"
                    value={form.cliente_nome}
                    onChange={(e) => setForm({ ...form, cliente_nome: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>E-mail *</Label>
                    <Input
                      data-testid="proposta-cliente-email"
                      type="email"
                      value={form.cliente_email}
                      onChange={(e) => setForm({ ...form, cliente_email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>CPF / CNPJ</Label>
                    <Input
                      data-testid="proposta-cliente-doc"
                      value={form.cliente_documento}
                      onChange={(e) => setForm({ ...form, cliente_documento: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Descrição da solução (aparece no PDF)</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={gerarSolucaoIA}
                      disabled={aiLoadingSolucao}
                      data-testid="ai-gerar-solucao"
                      className="text-xs"
                      style={{ borderColor: "#8b5cf6", color: "#7c3aed" }}
                      title="Gerar texto comercial usando IA (Claude)"
                    >
                      {aiLoadingSolucao ? (
                        <><Loader2 size={12} className="mr-1 animate-spin" /> Gerando…</>
                      ) : (
                        <><Sparkles size={12} className="mr-1" /> Gerar com IA</>
                      )}
                    </Button>
                  </div>
                  <Textarea
                    data-testid="proposta-descricao"
                    rows={6}
                    value={form.descricao_solucao}
                    onChange={(e) => setForm({ ...form, descricao_solucao: e.target.value })}
                    placeholder="Texto comercial introdutório da proposta... ou clique em 'Gerar com IA' para criar automaticamente"
                  />
                  <div className="text-[11px] text-muted-foreground mt-1">
                    💡 A IA usa os dados do lead + equipamentos já adicionados na Etapa 2 para gerar um texto comercial profissional. Você pode editar antes de gerar o PDF.
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={() => setStep(2)} data-testid="proposta-next-1" className="text-white" style={{ background: "#1B3A6B" }}>
                    Próximo: Equipamentos →
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2: Itens */}
            {step === 2 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-sm text-muted-foreground">Adicione os equipamentos ou serviços da proposta:</div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setShowInvSearch((s) => !s)} data-testid="proposta-toggle-inv">
                      📦 Buscar no inventário
                    </Button>
                    <Button size="sm" variant="outline" onClick={adicionarItem} data-testid="proposta-add-item">
                      <Plus size={14} className="mr-1" /> Adicionar item
                    </Button>
                  </div>
                </div>
                {showInvSearch && (
                  <Card className="p-3 space-y-2 bg-slate-50 border-blue-200 border" data-testid="inv-search-panel">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-6">
                        <Label className="text-xs">Buscar (serial, placa, observações)</Label>
                        <Input
                          data-testid="inv-q"
                          value={invQ}
                          onChange={(e) => setInvQ(e.target.value)}
                          placeholder="ex: 41962106 ou Jungheinrich"
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Propriedade</Label>
                        <select
                          data-testid="inv-prop"
                          value={invPropriedade}
                          onChange={(e) => setInvPropriedade(e.target.value)}
                          className="w-full border rounded h-9 px-2 text-sm bg-background"
                        >
                          <option value="">Todas</option>
                          <option value="logman">Logman</option>
                          <option value="jungheinrich">Jungheinrich</option>
                          <option value="locativo">Locativo</option>
                        </select>
                      </div>
                      <div className="col-span-3">
                        <Button size="sm" className="w-full text-white" style={{ background: "#1B3A6B" }} onClick={buscarInventario} disabled={invLoading} data-testid="inv-buscar">
                          {invLoading ? <Loader2 size={12} className="mr-1 animate-spin" /> : "🔍 "}
                          Buscar
                        </Button>
                      </div>
                    </div>
                    {invResults.length > 0 && (
                      <div className="max-h-60 overflow-y-auto space-y-1">
                        {invResults.map((eq) => (
                          <div key={eq.id} className="flex items-center justify-between p-2 bg-white rounded border hover:border-blue-400" data-testid={`inv-item-${eq.id}`}>
                            <div className="text-xs">
                              <div className="font-medium">{eq.nome_exibicao}</div>
                              <div className="text-muted-foreground">{eq.propriedade} · status: {eq.status}{eq.observacoes ? ` · ${eq.observacoes.slice(0, 60)}` : ""}</div>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => adicionarDoInventario(eq)} data-testid={`inv-add-${eq.id}`}>
                              <Plus size={12} className="mr-1" /> Adicionar
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {invResults.length === 0 && !invLoading && (
                      <div className="text-xs text-muted-foreground text-center py-2">
                        Clique em "Buscar" para localizar equipamentos do inventário real.
                      </div>
                    )}
                  </Card>
                )}
                {form.itens.length === 0 && (
                  <Card className="p-8 text-center text-muted-foreground">
                    Nenhum item adicionado.
                    <div className="text-xs mt-2">Clique em "Adicionar item" para começar.</div>
                  </Card>
                )}
                {form.itens.map((it, idx) => (
                  <Card key={idx} className="p-3 space-y-2" data-testid={`proposta-item-${idx}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 grid grid-cols-12 gap-2">
                        <div className="col-span-12">
                          <Label className="text-xs">Equipamento (catálogo)</Label>
                          <select
                            className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                            value={it.catalogo_id || ""}
                            onChange={(e) => aplicarCatalogo(idx, e.target.value)}
                            data-testid={`item-${idx}-catalogo`}
                          >
                            <option value="">— Item customizado —</option>
                            {catalogo.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.nome} {c.valor_mensal_sugerido ? `· ${fmtBRL(c.valor_mensal_sugerido)}/mês` : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-12">
                          <Label className="text-xs">Nome do item *</Label>
                          <Input
                            value={it.nome}
                            onChange={(e) => atualizarItem(idx, { nome: e.target.value })}
                            data-testid={`item-${idx}-nome`}
                          />
                        </div>
                        <div className="col-span-12">
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs">Descrição comercial (opcional · aparece no PDF)</Label>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => gerarDescricaoItemIA(idx)}
                              disabled={aiLoadingItem === idx || !it.nome?.trim()}
                              data-testid={`item-${idx}-ai-desc`}
                              className="h-6 px-2 text-[10px]"
                              style={{ color: "#7c3aed" }}
                              title="Gerar descrição comercial com IA"
                            >
                              {aiLoadingItem === idx ? (
                                <><Loader2 size={10} className="mr-1 animate-spin" /> Gerando…</>
                              ) : (
                                <><Sparkles size={10} className="mr-1" /> Sugerir com IA</>
                              )}
                            </Button>
                          </div>
                          <Textarea
                            rows={2}
                            value={it.descricao || ""}
                            onChange={(e) => atualizarItem(idx, { descricao: e.target.value })}
                            data-testid={`item-${idx}-descricao`}
                            placeholder="Aplicação prática, produtividade, economia..."
                            className="text-xs"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Qtd *</Label>
                          <Input
                            type="number"
                            min={1}
                            value={it.quantidade}
                            onChange={(e) => atualizarItem(idx, { quantidade: e.target.value })}
                            data-testid={`item-${idx}-qtd`}
                          />
                        </div>
                        <div className="col-span-4">
                          <Label className="text-xs">{form.tipo_contrato === "venda" ? "Valor unitário (R$)" : "Valor mensal (R$)"} *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            value={it.valor_mensal}
                            onChange={(e) => atualizarItem(idx, { valor_mensal: e.target.value })}
                            data-testid={`item-${idx}-valor`}
                          />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">Prazo (meses)</Label>
                          <select
                            className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                            value={it.prazo_meses}
                            onChange={(e) => atualizarItem(idx, { prazo_meses: e.target.value })}
                            data-testid={`item-${idx}-prazo`}
                          >
                            {PRAZOS.map((p) => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">Total</Label>
                          <div className="px-2 py-1.5 text-sm font-mono font-bold text-right" style={{ color: "#1B3A6B" }}>
                            {fmtBRL(calcTotalItem(it, form.tipo_contrato))}
                          </div>
                        </div>
                        {it.valor_referencia_compra && (
                          <div className="col-span-12 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded flex items-center gap-2">
                            <Badge variant="outline" className="bg-emerald-100">ROI ativo</Badge>
                            Valor referência compra: {fmtBRL(it.valor_referencia_compra)} · Manutenção: {fmtBRL(it.custo_manutencao_mensal_estimado)}/mês
                          </div>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removerItem(idx)} data-testid={`item-${idx}-remove`}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </div>
                  </Card>
                ))}
                {form.itens.length > 0 && (
                  <Card className="p-3 bg-slate-50">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{form.tipo_contrato === "locacao" ? "Valor mensal total:" : "Valor total:"}</span>
                      <span className="font-bold" style={{ color: "#1B3A6B" }}>{fmtBRL(form.tipo_contrato === "locacao" ? totalMensal : totalGeral)}</span>
                    </div>
                    {form.tipo_contrato === "locacao" && (
                      <div className="flex justify-between text-sm mt-1 pt-1 border-t">
                        <span className="text-muted-foreground">Valor total do contrato:</span>
                        <span className="font-bold text-lg" style={{ color: "#F97316" }}>{fmtBRL(totalGeral)}</span>
                      </div>
                    )}
                  </Card>
                )}
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)}>← Voltar</Button>
                  <Button onClick={() => setStep(3)} data-testid="proposta-next-2" className="text-white" style={{ background: "#1B3A6B" }} disabled={form.itens.length === 0}>
                    Próximo: Condições →
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3: Condições + finalizar */}
            {step === 3 && (
              <div className="space-y-3">
                <div>
                  <Label>Condição de pagamento cadastrada</Label>
                  <select
                    data-testid="proposta-condicao-cadastrada"
                    value={form.condicao_pagamento_id}
                    onChange={(e) => aplicarCondicao(e.target.value)}
                    className="w-full border rounded h-9 px-2 text-sm bg-background"
                  >
                    <option value="">— Selecione uma condição (opcional) —</option>
                    {condicoes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}{c.parcelas > 1 ? ` · ${c.parcelas}x` : ""}{c.entrada_percentual ? ` · entrada ${c.entrada_percentual}%` : ""}
                      </option>
                    ))}
                  </select>
                  {form.condicao_pagamento_id && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Você pode ajustar o texto da condição abaixo se precisar personalizar.
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Forma de pagamento</Label>
                    <select
                      data-testid="proposta-forma-pagamento"
                      value={form.forma_pagamento}
                      onChange={(e) => setForm({ ...form, forma_pagamento: e.target.value })}
                      className="w-full border rounded h-9 px-2 text-sm bg-white"
                    >
                      <option value="">— Selecione —</option>
                      <option value="pix_vista">À vista no PIX</option>
                      <option value="boleto">Boleto bancário</option>
                      <option value="parcelado">Parcelado</option>
                      <option value="transferencia">Transferência</option>
                      <option value="outros">Outros</option>
                    </select>
                  </div>
                  <div>
                    <Label>Telefone/WhatsApp do cliente</Label>
                    <Input
                      data-testid="proposta-telefone"
                      value={form.cliente_telefone}
                      onChange={(e) => setForm({ ...form, cliente_telefone: e.target.value })}
                      placeholder="55 81 99999-9999"
                    />
                  </div>
                </div>
                {form.forma_pagamento === "pix_vista" && (
                  <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800" data-testid="aviso-pix">
                    💳 Dados PIX da Locativo serão incluídos automaticamente no PDF.
                  </div>
                )}
                <FreteLocalFields
                  frete={form.frete}
                  local={form.local_operacao}
                  onFrete={(v) => setForm({ ...form, frete: v })}
                  onLocal={(v) => setForm({ ...form, local_operacao: v })}
                />
                <div>
                  <Label>Condições de pagamento</Label>
                  <Textarea
                    data-testid="proposta-condicoes"
                    rows={2}
                    value={form.condicoes_pagamento}
                    onChange={(e) => setForm({ ...form, condicoes_pagamento: e.target.value })}
                    placeholder="Ex: Pagamento via boleto bancário 30 dias após a NF"
                  />
                </div>
                <div>
                  <Label>Observações comerciais</Label>
                  <Textarea
                    data-testid="proposta-observacoes"
                    rows={3}
                    value={form.observacoes}
                    onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                    placeholder="Notas adicionais, manutenção, SLA, etc."
                  />
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)} disabled={saving}>← Voltar</Button>
                  <Button onClick={() => setStep(4)} data-testid="proposta-next-3" className="text-white" style={{ background: "#1B3A6B" }}>
                    Próximo: Modelo & Resumo →
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 4: Modelo de Contrato + Testemunhas + Resumo */}
            {step === 4 && (
              <div className="space-y-3">
                <div>
                  <Label>Modelo de contrato (Parte 2 do PDF)</Label>
                  <select
                    data-testid="proposta-modelo-contrato"
                    value={form.modelo_contrato_id}
                    onChange={(e) => setForm({ ...form, modelo_contrato_id: e.target.value })}
                    className="w-full border rounded h-9 px-2 text-sm bg-background"
                  >
                    <option value="">— Apenas Proposta (sem contrato anexo) —</option>
                    {modelosContrato
                      .filter((m) => !form.tipo_contrato || m.tipo_contrato === form.tipo_contrato || m.tipo_contrato === "outros")
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.nome}{m.padrao ? " ⭐" : ""} · {m.tipo_contrato}
                        </option>
                      ))}
                  </select>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {form.modelo_contrato_id ? (
                      <>📑 Proposta + Contrato serão gerados em um <strong>PDF único</strong> com 4 assinaturas (cliente, vendedor, 2 testemunhas).</>
                    ) : (
                      <>Sem modelo selecionado: apenas a Proposta comercial será gerada. Você pode adicionar um modelo em <strong>/app/configuracoes/modelos</strong>.</>
                    )}
                  </div>
                </div>

                {/* Testemunhas — opcionais, preenchidas pelo vendedor */}
                <div className="border rounded p-3 bg-slate-50">
                  <Label className="text-xs uppercase font-semibold mb-2 block text-slate-700">Testemunhas (opcional — para impressão do PDF)</Label>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <Input data-testid="t1-nome" value={form.testemunha_1_nome} onChange={(e) => setForm({ ...form, testemunha_1_nome: e.target.value })} placeholder="Testemunha 1 — Nome" />
                    <Input data-testid="t1-cpf" value={form.testemunha_1_cpf} onChange={(e) => setForm({ ...form, testemunha_1_cpf: e.target.value })} placeholder="Testemunha 1 — CPF" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="t2-nome" value={form.testemunha_2_nome} onChange={(e) => setForm({ ...form, testemunha_2_nome: e.target.value })} placeholder="Testemunha 2 — Nome" />
                    <Input data-testid="t2-cpf" value={form.testemunha_2_cpf} onChange={(e) => setForm({ ...form, testemunha_2_cpf: e.target.value })} placeholder="Testemunha 2 — CPF" />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">Deixe em branco para preenchimento manual no momento da assinatura.</p>
                </div>

                <Card className="p-4 bg-slate-50">
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Cliente:</span><strong>{form.cliente_nome}</strong></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Tipo:</span><Badge>{TIPOS.find((t) => t.v === form.tipo_contrato)?.l}</Badge></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Itens:</span>{form.itens.length}</div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Condição:</span>
                      <span>{condicoes.find((c) => c.id === form.condicao_pagamento_id)?.nome || "—"}</span>
                    </div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Modelo contrato:</span>
                      <span>{modelosContrato.find((m) => m.id === form.modelo_contrato_id)?.nome || "Sem contrato"}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t mt-2"><span className="text-muted-foreground">Valor total:</span><strong style={{ color: "#F97316", fontSize: 18 }}>{fmtBRL(totalGeral)}</strong></div>
                  </div>
                </Card>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(3)} disabled={saving}>← Voltar</Button>
                  <Button
                    onClick={salvarEGerar}
                    disabled={saving}
                    data-testid="proposta-finalizar"
                    className="text-white"
                    style={{ background: "#F97316" }}
                  >
                    {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Gerando PDF...</> : <><FileText size={14} className="mr-2" /> Criar Proposta + Gerar PDF</>}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
